import { useMutation } from '@apollo/client/react';
import { ControlledTextField } from '@drycut/controlled-components';
import { type BasicCustomerFragment, ValidatePhoneCustomerDocument } from '@drycut/graphql';
import { useEffect } from 'react';

type Props = {
  noLabel?: boolean;
  onSucessfulValidation?: (validated: BasicCustomerFragment) => void;
  setValidating?: (value: boolean) => void;
} & Omit<React.ComponentProps<typeof ControlledTextField>, 'label' | 'name'>;

export function ControlledPhoneField({
  setValidating,
  onSucessfulValidation,
  noLabel = false,
  ...props
}: Props): React.ReactElement {
  const [validatePhone, { loading }] = useMutation(ValidatePhoneCustomerDocument);
  const phoneRegex =
    /^(\+{0,})(\d{0,})([(]{1}\d{1,3}[)]{0,}){0,}(\s?\d+|\+\d{2,3}\s{1}\d+|\d+){1}[\s|-]?\d+([\s|-]?\d+){1,2}(\s){0,}$/gm;

  const defaultErrorMessage = 'Diese Telefonnummer ist ungültig';

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
    setValidating?.(loading);
  }, [loading, setValidating]);

  return (
    <ControlledTextField
      name="phone"
      label={noLabel ? '' : 'Telefonnummer'}
      type="tel"
      loading={loading}
      rules={{
        required: defaultErrorMessage,
        pattern: { value: phoneRegex, message: defaultErrorMessage },
        validate: {
          validateNumber: async (phoneNumber) => {
            const { data } = await validatePhone({ variables: { input: { phoneNumber } } });

            const validation = data?.validatePhoneCustomer;
            const errors = validation?.errors || [];

            if (errors.length > 0) {
              return defaultErrorMessage;
            } else {
              onSucessfulValidation?.(validation?.customer as BasicCustomerFragment);
            }
          },
        },
      }}
      placeholder="+41 78 1234567"
      {...props}
    />
  );
}
